import React, { useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Checkbox, Grid, TextField, Typography } from '@mui/material';
import { EventReviewProps } from '../EventReview';
import { createUpdateStaff, updateEventInfo } from '../../../services/EventService';
import { Event, Staff, StaffType } from '../../../services/Types';
import { Editor } from '../../../components/Editor/Editor';
import { copyObj } from '../../../services/Helper';
import { usePermissions } from '../../../hooks/usePermissions';
import { flexcol, flexItemCenter, flexItemLeft, flexItemRight } from '../../../global';
import { useEventValidation } from '../../../contexts/EventValidationContext';
import {
  filterStaffSuggestions,
  formatStaffSuggestionLabel,
  useStaffSuggestions,
} from '../../../hooks/useStaffSuggestions';

export const SpeakerAndStaffSupportReview: React.FC<EventReviewProps> = ({ eventData, handleEventChange }) => {
  const { eventIsUnlocked } = usePermissions(eventData || {} as Event);
  const { isFieldEnabled } = useEventValidation();
  const [error, setError] = useState<string | null>(null);
  const [selectedFromSuggestion, setSelectedFromSuggestion] = useState<boolean[]>([]);
  const suggestions = useStaffSuggestions(eventData?.programOfficeId);

  // Keep selectedFromSuggestion length in sync with staff array
  useEffect(() => {
    const len = eventData?.staff?.length ?? 0;
    setSelectedFromSuggestion(prev => {
      if (prev.length === len) return prev;
      if (prev.length > len) return prev.slice(0, len);
      return [...prev, ...Array(len - prev.length).fill(false)];
    });
  }, [eventData?.staff?.length]);

  const onChange = (field: string, index: number) => async (e: React.ChangeEvent<HTMLInputElement> | { target: { value: any } }) => {
    try {
      const eventValue = e.target.value;

      let newStaff: Staff[] = copyObj(eventData?.staff || [])
      newStaff[index] = {
        ...newStaff[index],
        [field]: eventValue
      }
  
      // update the event data in the parent component
      const syntheticEvent = {target: {value: newStaff}} as unknown as React.ChangeEvent<HTMLInputElement>;
      handleEventChange('staff')(syntheticEvent);
    } catch (error) {
      console.error(error)
      setError('Error updating event info: ' + error)
    }
  }

  const onSuggestionSelect = (index: number, suggestion: Staff) => {
    try {
      let newStaff: Staff[] = copyObj(eventData?.staff || [])
      newStaff[index] = {
        ...newStaff[index],
        name: suggestion.name,
        email: suggestion.email,
        phone: suggestion.phone,
        programOffice: suggestion.programOffice,
        jobTitle: suggestion.jobTitle,
      }
      const syntheticEvent = {target: {value: newStaff}} as unknown as React.ChangeEvent<HTMLInputElement>;
      handleEventChange('staff')(syntheticEvent);
    } catch (err) {
      console.error(err)
      setError('Error updating event info: ' + err)
    }
  }

  const onClearSuggestion = (index: number) => {
    try {
      let newStaff: Staff[] = copyObj(eventData?.staff || [])
      newStaff[index] = {
        ...newStaff[index],
        name: '',
        email: '',
        phone: '',
        programOffice: '',
        jobTitle: '',
      }
      const syntheticEvent = {target: {value: newStaff}} as unknown as React.ChangeEvent<HTMLInputElement>;
      handleEventChange('staff')(syntheticEvent);
    } catch (err) {
      console.error(err)
      setError('Error updating event info: ' + err)
    }
  }

  const onTbdChange = (field: keyof Event) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const eventValue = e.target.checked ? 1 : 0;
      
      // update the event data in the parent component
      const syntheticEvent = {target: {value: eventValue}} as unknown as React.ChangeEvent<HTMLInputElement>;
      handleEventChange(field)(syntheticEvent);
    } catch (error) {
      console.error(error)
      setError('Error updating event info: ' + error)
    }
  }

  //styles
  const styles = {
    container: {
      border: '1px solid #e0e0e0',
      padding: 2,
      borderRadius: 2,
      margin: 0,
      marginBottom: 2,
      backgroundColor: '#f5f5f5'
    }
  }

  return (
    <Grid container spacing={2}>
      {error && <Grid item xs={12}><Alert severity="error">{error}</Alert></Grid>}
      <Grid item xs={12}>
        {eventData?.staff?.map((staff, index) => (
          <Grid key={index} container spacing={2} sx={styles.container}>
            <Grid item xs={6}>
              <Autocomplete
                freeSolo
                disableClearable={!selectedFromSuggestion[index]}
                options={suggestions}
                getOptionLabel={formatStaffSuggestionLabel}
                isOptionEqualToValue={(option, value) =>
                  typeof option !== 'string' &&
                  typeof value !== 'string' &&
                  option.email === value.email
                }
                filterOptions={(options, { inputValue }) =>
                  filterStaffSuggestions(options, inputValue)
                }
                inputValue={staff.name || ''}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === 'input') {
                    const syntheticEvent = { target: { value: newInputValue } } as React.ChangeEvent<HTMLInputElement>;
                    onChange('name', index)(syntheticEvent);
                    setSelectedFromSuggestion(prev => {
                      const next = [...prev];
                      next[index] = false;
                      return next;
                    });
                  }
                  // 'clear' and 'reset' are owned by onChange to avoid double-update
                }}
                onChange={(_, newValue) => {
                  if (newValue && typeof newValue !== 'string') {
                    // Suggestion selected — fill all fields
                    onSuggestionSelect(index, newValue as Staff);
                    setSelectedFromSuggestion(prev => {
                      const next = [...prev];
                      next[index] = true;
                      return next;
                    });
                  } else if (typeof newValue === 'string') {
                    // freeSolo: user pressed Enter with a typed value
                    const syntheticEvent = { target: { value: newValue } } as React.ChangeEvent<HTMLInputElement>;
                    onChange('name', index)(syntheticEvent);
                    setSelectedFromSuggestion(prev => {
                      const next = [...prev];
                      next[index] = false;
                      return next;
                    });
                  } else {
                    // null — X (clear) button clicked
                    if (selectedFromSuggestion[index]) {
                      onClearSuggestion(index);
                    } else {
                      const syntheticEvent = { target: { value: '' } } as React.ChangeEvent<HTMLInputElement>;
                      onChange('name', index)(syntheticEvent);
                    }
                    setSelectedFromSuggestion(prev => {
                      const next = [...prev];
                      next[index] = false;
                      return next;
                    });
                  }
                }}
                disabled={!isFieldEnabled(!eventIsUnlocked)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Full Name"
                    disabled={!isFieldEnabled(!eventIsUnlocked)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                value={staff.staff_type}
                options={["MODERATOR","PRESENTER", "SUPPORT_STAFF"]}
                getOptionLabel={(option) => {
                  if (option === 'SUPPORT_STAFF') return 'Support Staff';
                  return option.charAt(0).toUpperCase() + option.slice(1).toLowerCase();
                }}
                renderInput={(params) => <TextField {...params} label="Role" disabled={!isFieldEnabled(!eventIsUnlocked)} />}
                onChange={(event, value) => {
                  const syntheticEvent = {
                    target: { 
                      value: value || ''
                    }
                  } as React.ChangeEvent<HTMLInputElement>;
                  onChange('staff_type', index)(syntheticEvent);
                }}
                disabled={!isFieldEnabled(!eventIsUnlocked)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Title"
                value={staff.jobTitle || ''}
                onChange={onChange('jobTitle', index)}
                disabled={!isFieldEnabled(!eventIsUnlocked)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Program Office"
                value={staff.programOffice || ''}
                onChange={onChange('programOffice', index)}
                disabled={!isFieldEnabled(!eventIsUnlocked)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Email"
                value={staff.email || ''}
                onChange={onChange('email', index)}
                disabled={!isFieldEnabled(!eventIsUnlocked)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Phone Number"
                value={staff.phone || ''}
                onChange={onChange('phone', index)}
                disabled={!isFieldEnabled(!eventIsUnlocked)}
              />
            </Grid>
          </Grid>
        ))}
        { eventData?.staff?.length === 0
          && !eventData?.tbd_moderators
          && !eventData?.tbd_presenters
          && !eventData?.tbd_supportstaff && (
            <Grid container spacing={2} sx={{...styles.container, width: '280px'}}>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{color: 'red'}}>Staff data is needed to complete the event.</Typography>
              </Grid>
            </Grid>
          )}
        { (eventData?.tbd_moderators || eventData?.tbd_presenters || eventData?.tbd_supportstaff) && (
          <Grid container spacing={2} sx={{...styles.container, width: '280px'}}>
          {[
            { key: 'tbd_moderators' as const, label: 'Moderators' },
            { key: 'tbd_presenters' as const, label: 'Presenters' },
            { key: 'tbd_supportstaff' as const, label: 'Support Staff' }
          ].map(({ key, label }) => {
            const value = eventData?.[key];
            return (
              <React.Fragment key={key}>
                <Grid item xs={6}>
                  <Typography sx={{textAlign: 'right', marginTop: '12px'}}>{label}:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Box style={ flexItemLeft }>
                    <Checkbox
                      checked={Boolean(value)}
                      onChange={onTbdChange(key)}
                      disabled={!isFieldEnabled(!eventIsUnlocked)}
                    />
                    <Typography>TBD</Typography>
                  </Box>
                </Grid>
              </React.Fragment>
              );
            })}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}; 